'use client'
import { Phone, X } from 'lucide-react'
import ActionButton from './ActionButton'
import { useState, useEffect, useRef } from 'react'
import { dispatch, useSelector } from '@/lib/store'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import { usePathname } from 'next/navigation'
import {
  FORM_TYPE_LAB_TEST,
  CHECKOUT_PAGE,
  ORDER_CONFIRMATION_PAGE,
  LEAD_PAGE,
  LEAD_PAGE_INITIAL,
} from '@/utils/constants'

const HIDDEN_PATH_PREFIXES = [
  CHECKOUT_PAGE,
  ORDER_CONFIRMATION_PAGE,
  LEAD_PAGE,
  LEAD_PAGE_INITIAL,
]

const GetAFreeCallBack = () => {
  const pathname = usePathname()
  const commonState = useSelector((state) => state.common) || {}

  // Track if user has closed the banner (survives re-renders within session)
  const [isShown, setIsShown] = useState(true)
  const [isClosing, setIsClosing] = useState(false)
  const hasClosedRef = useRef(false)
  const sectionRef = useRef<HTMLElement>(null)

  // Guard against state resets from re-renders - if user closed it, keep it closed
  useEffect(() => {
    if (hasClosedRef.current && isShown) {
      setIsShown(false)
    }
  }, [isShown])

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering parent onClick
    hasClosedRef.current = true
    setIsClosing(true)
    // Wait for animation to complete before removing from DOM
    setTimeout(() => {
      setIsShown(false)
    }, 500)
  }

  // Hide on the checkout journey and the lead landing pages; show everywhere else
  const isHiddenRoute = HIDDEN_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname?.startsWith(`${prefix}/`)
  )

  const isVisible =
    !isHiddenRoute &&
    // A page-level sticky bottom bar is on screen (PDP add-to-cart, etc). It is
    // anchored to bottom-0 just like this stripe, so yield to it.
    (commonState?.stickyBottomBarCount || 0) === 0 &&
    // When closed, don't render at all (removes from document flow)
    isShown

  useEffect(() => {
    const root = document.documentElement
    const el = sectionRef.current
    if (!isVisible || !el) {
      root.style.setProperty('--callback-stripe-height', '0px')
      return
    }
    const measure = () =>
      root.style.setProperty('--callback-stripe-height', `${el.offsetHeight}px`)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.setProperty('--callback-stripe-height', '0px')
    }
  }, [isVisible])

  if (!isVisible) return null

  return (
    <>
      {/* Spacer to prevent footer content from hiding behind fixed stripe */}
      <div className="h-14" />
      <section
        ref={sectionRef}
        className={`fixed z-40 bottom-0 left-0 right-0 bg-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] py-3 px-4 md:px-6 lg:px-8 transition-all duration-500 ease-out ${
          isClosing ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'
        }`}
      >
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Left section: Icon + Text */}
          <div
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() =>
              dispatch(
                updateCommonReducerData({
                  ...commonState,
                  requestCallbackModalIsOpen: {
                    modalIsOpen: true,
                    type: FORM_TYPE_LAB_TEST,
                  },
                })
              )
            }
          >
            <div className="flex-shrink-0 w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center">
              <Phone size={20} className="text-white" />
            </div>
            <p className="text-body-sm-medium sm:text-body-md-medium text-gray-800">
              Need Assistance? Call{' '}
              <span className="text-primary-500 font-semibold">
                93334-93334
              </span>{' '}
              or Request a Callback from our health advisor!
            </p>
          </div>

          {/* Right section: Input + Button + Close */}
          <div className="flex items-center gap-2 md:gap-3">
            <div
              className="hidden md:flex items-center gap-3 cursor-pointer"
              onClick={() =>
                dispatch(
                  updateCommonReducerData({
                    ...commonState,
                    requestCallbackModalIsOpen: {
                      modalIsOpen: true,
                      type: FORM_TYPE_LAB_TEST,
                    },
                  })
                )
              }
            >
              <input
                type="number"
                readOnly
                placeholder="Enter your phone number"
                className="bg-white border border-gray-300 rounded-full text-body-sm text-gray-800 w-52 focus:ring-0 focus:outline-none py-2.5 px-4 placeholder:text-gray-500"
                onChange={(e) => e.preventDefault()}
              />
              <ActionButton
                variant="primary"
                title="Get a free call back"
                className="whitespace-nowrap !rounded-full"
              />
            </div>
            <button
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              onClick={(e) => handleClose(e)}
              aria-label="Close"
            >
              <X size={18} className="text-gray-800" />
            </button>
          </div>
        </div>
      </section>
    </>
  )
}

export default GetAFreeCallBack
